import { useCallback, useMemo } from "react";
import React from "react";
import { useParams } from "react-router-dom";

import { SelectDealFolder } from "components/Prompts";
import { Rename } from "components/Prompts";
import { Create } from "components/Prompts/Create";
import { useActionHook, useIsViewerPermission, useModals, useNavigate, usePrompt, useRoleGuardHook } from "hooks";
import {
  IApiServiceError,
  useDealActions,
  useDocumentsActions,
  useFolderSelect,
  useTaskManageActions,
  useUserIdSelector,
  useViewActions
} from "store";
import { FolderKind, IFolder } from "store/types";

import { EntityType, RoleName } from "../../../constants";
import { useWorkspace } from "../WorkspaceContext";

interface UseWorkspaceFolderHookParams {
  folder: {
    id: number;
    title: string;
    is_private: boolean;
    parent_id: number | null;
    user_id: number;
    kind: FolderKind;
    deal_id: number | null;
  };
  refetchHandler?: () => Promise<void>;
}

export const useWorkspaceFolderHook = ({ folder, refetchHandler }: UseWorkspaceFolderHookParams) => {
  const {
    updateFolderAction,
    deleteFolderAction,
    createFolderAction,
    createFormAction,
    createGlobalWorkspaceFolderAction,
    getGlobalWorkspaceFoldersAction
  } = useDealActions();
  const userId = useUserIdSelector();
  const { deal_id: dealIdParam, folder_id: folderIdParam } = useParams<{ deal_id: string; folder_id: string }>();
  const dealId = dealIdParam ? Number(dealIdParam) : null;
  const folderId = folderIdParam ? Number(folderIdParam) : null;
  const parentFolder = useFolderSelect(folder?.parent_id ?? 0);
  const navigate = useNavigate();

  const {
    addDealFolder,
    addGlobalFolder,
    removeDealFolder,
    removeGlobalFolder,
    patchFolder,
    getOrLoadDealFolders,
    getFolderMetadata,
    refetchFolders,
    dispatchDetail
  } = useWorkspace();

  const { setShareDialog } = useViewActions();
  const { deleteModal, errorModal, createModal, filesUploadModal } = useModals();
  const { createDocumentAction } = useDocumentsActions();
  const { setCreateAction } = useTaskManageActions();
  const openPrompt = usePrompt();

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const isRoot = folder.id === -1;
  const effectiveDealId = isRoot ? dealId : folder.deal_id;

  const { action: updateFolder } = useActionHook(updateFolderAction);

  const onCreateNewFolder: (() => void) | undefined = useMemo(
    () => () =>
      createModal({
        action: async (name: string) => {
          if (effectiveDealId) {
            const newFolder = await createFolderAction({
              dealId: effectiveDealId,
              title: name,
              parent_id: isRoot ? (folderId ?? null) : folder.id
            });
            addDealFolder({ dealId: effectiveDealId, folder: newFolder });
          } else {
            const newFolder = await createGlobalWorkspaceFolderAction({
              title: name,
              parent_id: isRoot ? (folderId ?? null) : folder.id
            });
            addGlobalFolder({ folder: newFolder });
            await refetchFolders();
          }
          getFolderMetadata();
          await refetchHandler?.();
        },
        title: "Add Folder",
        submitButtonTitle: "Add"
      }),
    [
      isRoot,
      effectiveDealId,
      folder,
      folderId,
      createModal,
      createFolderAction,
      createGlobalWorkspaceFolderAction,
      addDealFolder,
      addGlobalFolder,
      refetchFolders,
      getFolderMetadata,
      refetchHandler
    ]
  );

  const onFileUpload = useCallback(async () => {
    filesUploadModal({
      dealId: dealId ?? undefined,
      folderId: folder?.id === -1 ? (folderId ?? undefined) : folder?.id,
      isGlobalWorkspace: !dealId,
      onSuccessCallback: async () => {
        getFolderMetadata();
        await refetchHandler?.();
      }
    });
  }, [dealId, folder?.id, folderId, refetchHandler, getFolderMetadata]);

  const onDropFiles = useCallback(
    (files: File[]) => {
      filesUploadModal({
        dealId: dealId ?? undefined,
        folderId: folder?.id,
        files,
        isGlobalWorkspace: !dealId,
        onSuccessCallback: async () => {
          getFolderMetadata();
          await refetchHandler?.();
        }
      });
    },
    [dealId, folder?.id, refetchHandler, getFolderMetadata]
  );

  const onRenameFolder = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (isRoot || !folder) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: folder.title }
      });
      if (title) {
        await updateFolder({ folderId: folder.id, dealId: effectiveDealId!, title });
        dispatchDetail({
          key: "patch",
          id: folder.id,
          entityType: EntityType.SECTION,
          patch: { title }
        });
        patchFolder({ dealId: effectiveDealId, folderId: folder.id, patch: { title } });
      }
    },
    [isRoot, folder, openPrompt, updateFolder, effectiveDealId, dispatchDetail, patchFolder]
  );

  const getPostDeleteNavigation = useCallback(() => {
    const isRootFolder = !folder.parent_id;
    const dealIdForNav = effectiveDealId ?? folder.deal_id ?? null;

    if (isRootFolder) {
      return dealIdForNav
        ? { route: "workspaceDeal" as const, params: { deal_id: dealIdForNav } }
        : { route: "workspaceFolders" as const, params: {} };
    }

    const parentId = folder.parent_id!;
    const search = { section: parentId };
    return dealIdForNav != null
      ? {
          route: "workspaceDealFolder" as const,
          params: { deal_id: dealIdForNav, folder_id: parentId },
          search
        }
      : { route: "workspaceFolder" as const, params: { folder_id: parentId }, search };
  }, [folder.parent_id, folder.deal_id, effectiveDealId]);

  const handleDelete = async (resolve?: Function) => {
    if (isRoot || !folder) return;
    try {
      const nav = getPostDeleteNavigation();
      await deleteFolderAction({ dealId: effectiveDealId ?? 0, folderId: folder.id });
      resolve?.(true);
      navigate({ ...nav });

      dispatchDetail({
        key: "delete",
        id: folder.id,
        entityType: EntityType.SECTION
      });

      if (effectiveDealId) {
        removeDealFolder({ dealId: effectiveDealId, folderId: folder.id });
      } else {
        removeGlobalFolder({ folderId: folder.id });
      }
    } catch (e) {
      errorModal({ msg: (e as IApiServiceError).message });
    }
  };

  const onDeleteFolder: (() => void) | undefined = useRoleGuardHook(() => {
    if (isRoot || !folder) return undefined;
    return deleteModal({
      element: "Delete folder permanently?",
      message: "This cannot be undone. All contents will be deleted.",
      onPressSuccess: "Delete permanently",
      action: async () => handleDelete()
    });
  }, [RoleName.Analyst, RoleName.Admin]);

  const onShareDialog: (() => void) | undefined = useRoleGuardHook(() => {
    if (isRoot || !folder) return undefined;
    setShareDialog({
      elements: [folder.id],
      type: "section_id",
      shareName: folder.title,
      dealId: folder.deal_id,
      itemToShare: folder.is_private ? null : folder.id,
      cb: () => {
        refetchHandler?.();
      }
    });
  }, [RoleName.Admin, RoleName.Analyst]);

  const checkOnSetPrivatePermission = () => {
    if (isRoot || !folder) return false;
    if (folder.user_id === userId && !folder.parent_id) {
      return true;
    }
    if (folder.user_id === userId && folder.parent_id && !parentFolder?.is_private) {
      return true;
    }
    return false;
  };

  const onPrivate = useCallback(async () => {
    if (isRoot || !folder) return;
    try {
      await updateFolder({
        folderId: folder.id,
        dealId: effectiveDealId!,
        is_private: !folder.is_private
      });
      dispatchDetail({
        key: "patch",
        id: folder.id,
        entityType: EntityType.SECTION,
        patch: { is_private: !folder.is_private }
      });
      patchFolder({ dealId: effectiveDealId, folderId: folder.id, patch: { is_private: !folder.is_private } });
    } catch (error) {
      errorModal({ msg: (error as IApiServiceError).message });
    }
  }, [folder, isRoot, effectiveDealId, updateFolder, dispatchDetail, patchFolder, errorModal]);

  const onSetPrivate: (() => void) | undefined = useRoleGuardHook(
    checkOnSetPrivatePermission() ? onPrivate : undefined,
    [RoleName.Admin, RoleName.Analyst]
  );

  const onAddForm = useCallback(async () => {
    let selectedFolderId: number | null = isRoot || !folder || folder.kind !== "folder" ? folderId : folder.id;

    if (!selectedFolderId) {
      const folders = effectiveDealId
        ? await getOrLoadDealFolders(effectiveDealId)
        : await getGlobalWorkspaceFoldersAction();

      const selectedFolder = await openPrompt({
        Component: SelectDealFolder,
        props: {
          dealId: effectiveDealId!,
          title: "Select Folder",
          folders,
          selectedFolderId,
          onFolderCreate: (newFolder: IFolder) => {
            if (effectiveDealId) {
              addDealFolder({ dealId: effectiveDealId, folder: newFolder });
            } else {
              addGlobalFolder({ folder: newFolder });
            }
          }
        }
      });

      if (!selectedFolder) return;
      selectedFolderId = selectedFolder.id;
    }
    if (!selectedFolderId) return;

    const newFormTitle = await openPrompt({
      Component: Create,
      props: {
        title: "Add a new form"
      }
    });
    if (!newFormTitle) return;

    const createdForm = await createFormAction({
      dealId: effectiveDealId!,
      sectionId: selectedFolderId,
      title: newFormTitle
    });

    getFolderMetadata();
    await refetchHandler?.();

    if (effectiveDealId) {
      navigate({
        route: "workspaceDealFolderForm",
        params: { deal_id: effectiveDealId, folder_id: selectedFolderId, form_id: createdForm.id }
      });
    } else {
      navigate({
        route: "workspaceFolderForm",
        params: { folder_id: selectedFolderId, form_id: createdForm.id }
      });
    }
  }, [
    isRoot,
    folder,
    folderId,
    effectiveDealId,
    getOrLoadDealFolders,
    getGlobalWorkspaceFoldersAction,
    addDealFolder,
    addGlobalFolder,
    openPrompt,
    createFormAction,
    getFolderMetadata,
    refetchHandler
  ]);

  const onAddDocument = useCallback(async () => {
    let selectedFolderId: number | null = isRoot || !folder || folder.kind !== "folder" ? folderId : folder.id;

    if (!selectedFolderId) {
      const folders = effectiveDealId
        ? await getOrLoadDealFolders(effectiveDealId)
        : await getGlobalWorkspaceFoldersAction();

      const selectedFolder = await openPrompt({
        Component: SelectDealFolder,
        props: {
          dealId: effectiveDealId!,
          title: "Select Folder",
          folders,
          selectedFolderId,
          onFolderCreate: (newFolder: IFolder) => {
            if (effectiveDealId) {
              addDealFolder({ dealId: effectiveDealId, folder: newFolder });
            } else {
              addGlobalFolder({ folder: newFolder });
            }
          }
        }
      });

      if (!selectedFolder) return;
      selectedFolderId = selectedFolder.id;
    }
    if (!selectedFolderId) return;

    const newDocumentTitle = await openPrompt({
      Component: Create,
      props: {
        title: "Add a new document"
      }
    });
    if (!newDocumentTitle) return;

    const createdDocument = await createDocumentAction({
      section_id: selectedFolderId,
      title: newDocumentTitle,
      disableDispatch: true,
      ...(effectiveDealId && { deal_id: effectiveDealId })
    });

    getFolderMetadata();
    await refetchHandler?.();

    if (effectiveDealId) {
      navigate({
        route: "workspaceDealFolderDocument",
        params: { deal_id: effectiveDealId, folder_id: selectedFolderId, document_id: createdDocument.id }
      });
    } else {
      navigate({
        route: "workspaceFolderDocument",
        params: { folder_id: selectedFolderId, document_id: createdDocument.id }
      });
    }
  }, [
    isRoot,
    folder,
    folderId,
    effectiveDealId,
    getOrLoadDealFolders,
    getGlobalWorkspaceFoldersAction,
    addDealFolder,
    addGlobalFolder,
    openPrompt,
    createDocumentAction,
    getFolderMetadata,
    refetchHandler
  ]);

  const onAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_id: folder.id,
      entity_type: "section"
    });
  }, [folder.id]);

  const isAvailableRoleWithPermissions = isAnalystDealsViewerPermission
    ? [RoleName.Admin]
    : [RoleName.Admin, RoleName.Analyst];

  const onCreateNewFolderWithRole = useRoleGuardHook(onCreateNewFolder, isAvailableRoleWithPermissions);
  const onAddFormWithRole = useRoleGuardHook(onAddForm, isAvailableRoleWithPermissions);
  const onAddDocumentWithRole = useRoleGuardHook(onAddDocument, isAvailableRoleWithPermissions);
  const onFileUploadWithRole = useRoleGuardHook(onFileUpload, isAvailableRoleWithPermissions);
  const onDropFilesWithRole = useRoleGuardHook(onDropFiles, isAvailableRoleWithPermissions);
  const onRenameFolderWithRole = useRoleGuardHook(onRenameFolder, isAvailableRoleWithPermissions);
  const onDeleteFolderWithRole = useRoleGuardHook(onDeleteFolder, isAvailableRoleWithPermissions);
  const onShareDialogWithRole = useRoleGuardHook(onShareDialog, isAvailableRoleWithPermissions);
  const onSetPrivateWithRole = useRoleGuardHook(onSetPrivate, isAvailableRoleWithPermissions);
  const onAddTaskWithRole = useRoleGuardHook(onAddTask, isAvailableRoleWithPermissions);

  return {
    onCreateNewFolder: onCreateNewFolderWithRole,
    onAddForm: onAddFormWithRole,
    onAddDocument: onAddDocumentWithRole,
    onFileUpload: onFileUploadWithRole,
    onDropFiles: onDropFilesWithRole,
    onRenameFolder: onRenameFolderWithRole,
    onDeleteFolder: onDeleteFolderWithRole,
    onShareDialog: onShareDialogWithRole,
    onSetPrivate: onSetPrivateWithRole,
    checkOnSetPrivatePermission,
    onAddTask: onAddTaskWithRole
  };
};
